import { defineStore } from "pinia"
import { SCANNED_IDENTIFIER_LIFETIME } from "src/util/constants/configurations"
import {
  Identifier,
  IdentifierAssociationType,
  ProofOfPresence,
} from "src/util/types/dataModels/identifier"

export interface ScanStoreType {
  identifier: Identifier | null
  scannedTime: Date | null
}

/**
 * **You should not manually update the state and should use the getter/setters.**
 */
export const useScanStore = defineStore("pinia-scanStore", {
  persist: false,

  state: (): ScanStoreType => ({
    identifier: null,
    scannedTime: null,
  }),

  getters: {
    /**
     * Returns the identifier data if it is present AND is not expired. Otherwise it returns null.
     */
    getIdentifier(): Identifier | null {
      if (this.isScanAlive) return null
      return this.identifier
    },

    getProofOfPresence(): ProofOfPresence | undefined {
      if (this.identifier === null || this.scannedTime === null) return

      return {
        type: this.identifier.type,
        value: this.identifier.data,
        dt: this.scannedTime.toJSON(),
      }
    },

    /**
     * Check to see if the last scan is still in its lifetime or is already expired.
     */
    isScanAlive(state): boolean {
      // There is no scan, no point to continue
      if (state.scannedTime === null) return false

      return Date.now() < state.scannedTime.getTime() + SCANNED_IDENTIFIER_LIFETIME
    },
  },

  actions: {
    /**
     * Overrides the currently saved state and automatically populates all additonal fields based on provided identifier.
     * @param newIdentifier
     */
    setIdentifier(newIdentifier: Identifier) {
      // We should always try to override the data, since the identifier might be with the same ID,
      // but its internal data might be different. F.x. it is now assigned to extra entities.
      this.identifier = newIdentifier
      this.scannedTime = new Date()
    },

    /**
     * If the saved scan matches with the provided entity, then don't do anything.
     * However, if you are accessing an actionable entity that is different than the scanned one, then reset the store.
     *
     * Intended to only be used on viewing actionable entity pages, since we can "deduce" that the user is:
     * 1. Working on an item that they possibly scanned, so we don't touch the store.
     * 2. Working on an item, different than the one scanned before, so we want to reset the store.
     *
     * @param scannedEntityType
     * @param scannedEntityId
     */
    revalidateState(scannedEntityType: IdentifierAssociationType, scannedEntityId: string) {
      if (this.isScanValid(scannedEntityType, scannedEntityId)) return

      // We are accessing an entity, different than the scanned, or the scan data is dead,
      // so we get rid of the currently saved state.
      this.$reset()
    },

    /**
     * Is the last scanned identifier alive and does it belong to the entity the user is currently working on.
     * @param scannedEntityType
     * @param scannedEntityId
     */
    isScanValid(scannedEntityType: IdentifierAssociationType, scannedEntityId: string): boolean {
      if (this.identifier === null || !this.isScanAlive) return false

      switch (scannedEntityType) {
        case "item":
          if (this.identifier.item === scannedEntityId) return true

        case "area":
          if (this.identifier.area === scannedEntityId) return true

        case "location":
          if (this.identifier.location === scannedEntityId) return true
      }

      return false
    },
  },
})
